/**
 * Copy Shift Dialog Component
 * 
 * Extended dialog for copying shifts to multiple dates.
 * Extracted from Shifts.jsx for modularity.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, Copy,
  CalendarRange, Loader2
} from 'lucide-react';
import { format, getISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { formatDateDE } from './index';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';

const CopyShiftDialog = ({
  open,
  onOpenChange,
  shiftToCopy,
  entityType = 'shift',
  copyDays,
  copyWeekStart,
  copyDateRange,
  setCopyDateRange,
  saving,
  // Maßnahmen für „zwischen Maßnahmen kopieren"
  massnahmen = [],
  targetMassnahmeId,
  setTargetMassnahmeId,
  // Verschieben statt Kopieren
  deleteSourceAfterCopy,
  setDeleteSourceAfterCopy,
  // Actions
  selectWeekForCopy,
  selectNextWeek,
  selectNextTwoWeeks,
  selectUntilMonthEnd,
  clearCopyDays,
  selectFromDateRange,
  getWeekdaysInRange,
  handleCopyPrevWeek,
  handleCopyNextWeek,
  getCopyWeekDays,
  toggleCopyDay,
  handleCopyShift,
}) => {
  if (!shiftToCopy) return null;
  const isActivity = entityType === 'activity';
  const noun = isActivity ? 'Aktivität' : 'Einsatz';
  const nounPlural = isActivity ? 'Aktivitäten' : 'Einsätze';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{noun} kopieren</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Shift / Activity Info */}
          <div className="p-3 bg-muted rounded-lg">
            {isActivity ? (
              <>
                <p className="font-medium">{shiftToCopy.activity_label || shiftToCopy.activity_type}</p>
                <p className="text-sm text-muted-foreground">
                  {shiftToCopy.massnahme_name}
                  {shiftToCopy.participant_count ? ` • ${shiftToCopy.participant_count} Teilnehmer` : ''}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Original: {formatDateDE(shiftToCopy.date)}
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">{shiftToCopy.employee_name}</p>
                <p className="text-sm text-muted-foreground">
                  {shiftToCopy.start_time} - {shiftToCopy.end_time}
                  {shiftToCopy.room_name && ` • ${shiftToCopy.room_name}`}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Original: {formatDateDE(shiftToCopy.date)}
                </p>
              </>
            )}
          </div>

          {/* Ziel-Maßnahme — fuer „zwischen Maßnahmen kopieren" */}
          {massnahmen && massnahmen.length > 1 && setTargetMassnahmeId && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Ziel-Maßnahme</Label>
              <Select
                value={targetMassnahmeId || shiftToCopy.massnahme_id}
                onValueChange={setTargetMassnahmeId}
              >
                <SelectTrigger data-testid="copy-target-massnahme">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {massnahmen.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                        {m.short_code ? `${m.short_code} – ` : ''}{m.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {targetMassnahmeId && targetMassnahmeId !== shiftToCopy.massnahme_id && (
                <p className="text-xs text-amber-600">
                  ↪ Wird in eine andere Maßnahme kopiert
                </p>
              )}
            </div>
          )}

          {/* Verschieben statt Kopieren */}
          {setDeleteSourceAfterCopy && (
            <label
              htmlFor="copy-delete-source"
              className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                id="copy-delete-source"
                checked={!!deleteSourceAfterCopy}
                onCheckedChange={(v) => setDeleteSourceAfterCopy(v === true)}
                data-testid="copy-delete-source-toggle"
                className="mt-0.5"
              />
              <div className="flex-1 space-y-0.5">
                <div className="text-sm font-medium leading-none">
                  Verschieben statt Kopieren
                </div>
                <p className="text-xs text-muted-foreground">
                  Original-{noun.toLowerCase()} nach erfolgreichem Kopieren löschen.
                  {deleteSourceAfterCopy && (
                    <span className="block mt-1 text-amber-600 font-medium">
                      ⚠ Achtung: Der ursprüngliche {noun} vom {formatDateDE(shiftToCopy.date)} wird gelöscht.
                    </span>
                  )}
                </p>
              </div>
            </label>
          )}

          {/* Quick Copy Buttons */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Schnell-Kopieren</Label>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={selectWeekForCopy}>
                <Calendar className="h-3 w-3 mr-1" />
                Diese Woche
              </Button>
              <Button variant="outline" size="sm" onClick={selectNextWeek}>
                <ChevronRight className="h-3 w-3 mr-1" />
                Nächste Woche
              </Button>
              <Button variant="outline" size="sm" onClick={selectNextTwoWeeks}>
                <ChevronRight className="h-3 w-3 mr-1" />
                Nächste 2 Wochen
              </Button>
              <Button variant="outline" size="sm" onClick={selectUntilMonthEnd}>
                <CalendarRange className="h-3 w-3 mr-1" />
                Bis Monatsende
              </Button>
              {copyDays.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearCopyDays} className="text-red-500 hover:text-red-700">
                  <Trash2 className="h-3 w-3 mr-1" />
                  Auswahl löschen
                </Button>
              )}
            </div>
          </div>

          {/* Date Range Selection */}
          <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
            <Label className="text-sm font-medium">Datumsbereich auswählen</Label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:flex-wrap">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Label className="text-xs text-muted-foreground shrink-0">Von:</Label>
                <DatePickerDE
                  value={copyDateRange.start}
                  onChange={(v) => setCopyDateRange(prev => ({ ...prev, start: v }))}
                  placeholder="Von"
                  className="flex-1 sm:w-36 sm:flex-none h-8 text-sm"
                />
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Label className="text-xs text-muted-foreground shrink-0">Bis:</Label>
                <DatePickerDE
                  value={copyDateRange.end}
                  onChange={(v) => setCopyDateRange(prev => ({ ...prev, end: v }))}
                  placeholder="Bis"
                  className="flex-1 sm:w-36 sm:flex-none h-8 text-sm"
                />
              </div>
              <Button variant="secondary" size="sm" onClick={selectFromDateRange} className="w-full sm:w-auto">
                <Plus className="h-3 w-3 mr-1" />
                Hinzufügen
              </Button>
            </div>
            {copyDateRange.start && copyDateRange.end && (
              <p className="text-xs text-muted-foreground">
                {getWeekdaysInRange(copyDateRange.start, copyDateRange.end).length} Werktage im Bereich
              </p>
            )}
          </div>

          {/* Week Navigation */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Wochenauswahl</Label>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopyPrevWeek}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium min-w-[140px] text-center">
                  KW {copyWeekStart ? getISOWeek(copyWeekStart) : ''} • {copyWeekStart ? format(copyWeekStart, 'dd.MM.', { locale: de }) : ''}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopyNextWeek}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {getCopyWeekDays().map(day => {
                const dayStr = format(day, 'yyyy-MM-dd');
                // Cross-Measure-Copy: derselbe Tag darf wiederverwendet werden,
                // wenn in eine *andere* Maßnahme kopiert wird.
                const isCrossMeasure = targetMassnahmeId && targetMassnahmeId !== shiftToCopy.massnahme_id;
                const isOriginalDay = dayStr === shiftToCopy.date && !isCrossMeasure;
                const isSelected = copyDays.includes(dayStr);
                const isPast = day < new Date() && format(day, 'yyyy-MM-dd') < format(new Date(), 'yyyy-MM-dd');
                
                return (
                  <button
                    key={dayStr}
                    onClick={() => !isOriginalDay && toggleCopyDay(dayStr)}
                    disabled={isOriginalDay}
                    className={`p-2 rounded-lg border text-center transition-all ${
                      isOriginalDay 
                        ? 'bg-amber-100 text-amber-700 border-amber-300 cursor-not-allowed' 
                        : isSelected 
                          ? 'bg-primary text-primary-foreground border-primary' 
                          : isPast
                            ? 'bg-muted/30 text-muted-foreground hover:border-primary/30'
                            : 'hover:border-primary/50'
                    }`}
                  >
                    <div className="text-xs">{format(day, 'EEE', { locale: de })}</div>
                    <div className="font-medium">{format(day, 'd.')}</div>
                    <div className="text-[10px] text-muted-foreground">{format(day, 'MMM', { locale: de })}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Days Summary */}
          {copyDays.length > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">
                  {copyDays.length} Tag{copyDays.length > 1 ? 'e' : ''} ausgewählt
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {copyDays.sort().slice(0, 10).map(dayStr => (
                  <Badge key={dayStr} variant="secondary" className="text-xs">
                    {formatDateDE(dayStr)}
                  </Badge>
                ))}
                {copyDays.length > 10 && (
                  <Badge variant="outline" className="text-xs">
                    +{copyDays.length - 10} weitere
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Abbrechen</Button>
            <Button onClick={handleCopyShift} disabled={saving || copyDays.length === 0} className="w-full sm:w-auto" data-testid="copy-confirm-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copyDays.length > 0
                ? (deleteSourceAfterCopy
                    ? `Auf ${copyDays.length} Tag${copyDays.length > 1 ? 'e' : ''} verschieben`
                    : `Auf ${copyDays.length} Tag${copyDays.length > 1 ? 'e' : ''} kopieren`)
                : 'Tage auswählen'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CopyShiftDialog;
